<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { $getUserItems } from '@/api/items'
import DivTable from '@/components/vuetify-custom-components/DivTable.vue'
import { TableOptions } from '@/models/general/table'
import {boolean} from "mathjs";

interface IProps {
  maxItemsCount?: number,
}

const props = withDefaults(defineProps<IProps>(), {
  maxItemsCount: 0,
})

const vuetifyDisplay = useDisplay()

const itemTableHeaders = [
  { key: 'line_no', title: 'Line No', sortable: false },
  { key: 'status', title: 'Status', sortable: false },
  { key: 'campaign', title: 'Project', sortable: false },
  { key: 'work_order_no', title: 'Work Order', sortable: false },
  { key: 'description', title: 'Description', sortable: false },
  { key: 'actions', title: '', sortable: false },
]

const itemTableOptions = ref<TableOptions>({
  itemsPerPage: 20,
  page: 1,
  sortBy: []
})

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
]

const isMounted = ref<boolean>(false)

const items = ref<any[]>([])
const itemsLoading = ref<boolean>(false)
const itemsCount = ref<number>(0)
const currentPage = ref(1)

const showLineItemReviewDialog = ref<boolean>(false)
const selectedLineItem = ref<{
  campaign_id: string | number | null,
  work_order_id: string | number | null,
  id: string | number | null
}>({
  campaign_id: null,
  work_order_id: null,
  id: null
})

const perPage = computed<number>(() => {
  return vuetifyDisplay?.lgAndUp ? 20 : 10
})

const tableHeight = computed<number | string>(() => {
  if (vuetifyDisplay?.lgAndUp) {
    if (props.maxItemsCount === 0) {
      return 100
    }
    return 500
  }
  return ''
})

const gerOrdering = () => {
  // Define default ordering
  let ordering = 'id'

  // Check if sortBy is defined and has at least one item
  if (itemTableOptions.value.sortBy.length > 0) {
    const sortBy = itemTableOptions.value.sortBy[0]
    const sortByKey = sortBy.key
    const sortOrder = sortBy.order === 'desc' ? '-' : '' // Determine the sorting order

    ordering = `${sortOrder}${sortByKey}`
  }

  return ordering
}

const getItems = async () => {
  itemsLoading.value = true

  const ordering = undefined

  try {
    const res = await $getUserItems({
      page: itemTableOptions.value.page,
      per_page: itemTableOptions.value.itemsPerPage,
      ordering: ordering,
    })

    items.value = res.data.results
    itemsCount.value = res.data.count
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e?.response?.status === 404) {
      itemTableOptions.value.page = 1
      await getItems()
    } else {
      console.log(e)
    }
  } finally {
    itemsLoading.value = false
  }
}

onMounted(async () => {
  await getItems()

  isMounted.value = true
})

// properties & methods are public
defineExpose({
  itemsCount,
})

const onUpdateTableOptions = async ({ page, itemsPerPage, sortBy }: TableOptions) => {
  itemTableOptions.value.page = page
  itemTableOptions.value.itemsPerPage = itemsPerPage
  itemTableOptions.value.sortBy = sortBy
  if (isMounted.value) {
    await getItems()
  }
}
</script>

<template>
  <VCard
    variant="outlined" 
    class="card-with-shadow"
    title="Branding Items Assigned To You"
  >
    <VCardText>
      <VOverlay
        v-model="itemsLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>
      <DivTable
        v-model:page="itemTableOptions.page"
        :items="[ ...items ]"
        :items-length="parseInt(String(itemsCount), 10)"
        :headers="itemTableHeaders"
        :loading="!!itemsLoading"
        grid-id="home-user-items-table"
        grid-storage-id="home-user-items-list"
        no-data-text="Items not found"
        hover
        :items-per-page-options="itemsPerPageOptions"
        :items-per-page="itemTableOptions.itemsPerPage"
        :sort-by="itemTableOptions.sortBy"
        @update:options="onUpdateTableOptions"
      >
        <template #item.actions="{ item }">
          <VBtn
            size="small"
            @click="
              selectedLineItem = {
                campaign_id: item.raw.campaign_id,
                work_order_id: item.raw.work_order_id,
                id: item.raw.id
              };
              showLineItemReviewDialog = true
            "
          >
            View
          </VBtn>
        </template>
      </DivTable>
    </VCardText>
    <LineItemReviewDialog
      v-model="showLineItemReviewDialog"
      :project-id="selectedLineItem.campaign_id"
      :work-order-id="selectedLineItem.work_order_id"
      :line-item-id="selectedLineItem.id"
      @uploaded="getItems()"
    />
  </VCard>
</template>
